import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import siteText from '../assets/siteText.json';
import SEO from '../components/SEO';
import './Review.css';

// Guest review form, linked from post-stay emails as /review?ref=18772/01/36&name=Katie.
// Not in the nav or sitemap, and marked noindex. Submissions are emailed to us for approval.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MIN_TEXT = 20;
const MAX_TEXT = 2000;

// The current month and the 17 before it, newest first, e.g. "October 2026".
function recentMonths() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 18; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${MONTHS[d.getMonth()]} ${d.getFullYear()}`);
  }
  return out;
}

function Review() {
  const [searchParams] = useSearchParams();
  const guestName = (searchParams.get('name') || '').trim().slice(0, 50);
  const bookingRef = (searchParams.get('ref') || '').trim().slice(0, 30);
  const months = useMemo(recentMonths, []);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ text: '', name: guestName, from: '', month: '', publish: true, website: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!rating) {
      setError('Please choose a star rating.');
      return;
    }
    if (form.text.trim().length < MIN_TEXT) {
      setError(`Please write at least ${MIN_TEXT} characters about your stay.`);
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch('/api/review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, rating, ref: bookingRef }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Sorry, something went wrong.');
      setStatus('done');
      window.scrollTo(0, 0);
    } catch (err) {
      setStatus('error');
      setError(`${err.message} You can also email us at figtree@dhowell.net.`);
    }
  };

  const shown = hover || rating;

  return (
    <div className="review-page">
      <SEO title="Review your stay | Fig Tree Cottage" path="/review" noindex />
      <div className="review-card">
        {status === 'done' ? (
          <div role="status">
            <h1>Thank you, we really appreciate it.</h1>
            {rating === 5 && siteText.googleReviewUrl && (
              <p>
                If you have a moment, would you also share it on Google? It helps other guests find us.
                <br />
                <a className="review-google" href={siteText.googleReviewUrl} target="_blank" rel="noopener noreferrer">
                  Review us on Google
                </a>
              </p>
            )}
          </div>
        ) : (
          <>
            <h1>{guestName ? `Thanks for staying, ${guestName}` : 'Review your stay'}</h1>
            <p style={{ margin: 0 }}>
              We'd love to hear how your stay at Fig Tree Cottage went. It only takes a minute, and there's nothing to sign in to.
            </p>

            <form onSubmit={handleSubmit}>
              <input type="hidden" name="ref" value={bookingRef} />

              <fieldset className="review-stars" onMouseLeave={() => setHover(0)}>
                <legend>Your rating</legend>
                <div className="review-stars-row">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <React.Fragment key={n}>
                      <input
                        type="radio"
                        id={`rating-${n}`}
                        name="rating"
                        value={n}
                        checked={rating === n}
                        onChange={() => setRating(n)}
                        required
                      />
                      <label
                        htmlFor={`rating-${n}`}
                        className={n <= shown ? 'is-on' : ''}
                        onMouseEnter={() => setHover(n)}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        title={`${n} star${n > 1 ? 's' : ''}`}
                      >
                        <span aria-hidden="true">★</span>
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              </fieldset>

              <label className="review-field">
                Tell future guests about your stay
                <textarea
                  name="text"
                  value={form.text}
                  onChange={update}
                  required
                  minLength={MIN_TEXT}
                  maxLength={MAX_TEXT}
                />
                <div className="review-counter" aria-live="polite">
                  {form.text.trim().length < MIN_TEXT
                    ? `${MIN_TEXT - form.text.trim().length} more characters, please`
                    : `${form.text.length} / ${MAX_TEXT}`}
                </div>
              </label>

              <label className="review-field">
                First name
                <input type="text" name="name" value={form.name} onChange={update} required maxLength={50} autoComplete="given-name" />
              </label>

              <label className="review-field">
                Where are you from? <span className="review-hint">(optional)</span>
                <input type="text" name="from" value={form.from} onChange={update} maxLength={80} placeholder="e.g. Bristol" />
              </label>

              <label className="review-field">
                Month of stay <span className="review-hint">(optional)</span>
                <select name="month" value={form.month} onChange={update}>
                  <option value="">Choose a month</option>
                  {months.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>

              <label className="review-checkbox">
                <input type="checkbox" name="publish" checked={form.publish} onChange={update} />
                <span>Happy for this to be published on the website with my first name</span>
              </label>

              {/* Honeypot for bots: hidden from people and from screen readers */}
              <div className="review-hp" aria-hidden="true">
                <label>
                  Website
                  <input type="text" name="website" value={form.website} onChange={update} tabIndex={-1} autoComplete="off" />
                </label>
              </div>

              <button type="submit" className="review-submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send my review'}
              </button>
              {error && <div className="review-error" role="alert">{error}</div>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Review;
